// VotingEventForm.tsx - Combined Create/Edit Component with Preview and CSV Import
import React, { useState } from "react";
import {
  Create,
  Edit,
  SimpleForm,
  TextInput,
  DateTimeInput,
  BooleanInput,
  NumberInput,
  NumberField,
  DateField,
  required,
  minValue,
  useRedirect,
  Toolbar,
  SaveButton,
  DeleteButton,
  useEditContext,
  EditProps,
  CreateProps,
  useNotify,
  useDataProvider,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";
import Papa from "papaparse";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import TitleStyleInput from "../../components/TitlestyleInput";

interface CsvRow {
  artistName?: string;
  ArtistName?: string;
  displayName?: string;
  DisplayName?: string;
  assetSound?: string;
  AssetSound?: string;
  sound?: string;
  Sound?: string;
}

// CSV Import Component
const VotableItemsCsvImport = () => {
  const { record } = useEditContext();
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setImporting(true);

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            // Step 1: Delete all existing votable items
            try {
              const { data: existingItems } = await dataProvider.getList(
                `votingEvents/${record.id}/votableItems`,
                {
                  pagination: { page: 1, perPage: 1000 },
                  sort: { field: "id", order: "ASC" },
                  filter: {},
                }
              );

              if (existingItems && existingItems.length > 0) {
                const deletePromises = existingItems.map((item) =>
                  dataProvider.delete(
                    `votingEvents/${record.id}/votableItems`,
                    {
                      id: item.id,
                      previousData: item,
                    }
                  )
                );
                await Promise.all(deletePromises);
                notify(`Removed ${existingItems.length} existing items`, {
                  type: "info",
                });
              }
            } catch (error) {
              console.error("Error deleting existing items:", error);
              // Continue with import even if delete fails
            }

            // Step 2: Deduplicate by artistName + assetSound
            const seen = new Set<string>();
            const uniqueRows = (results.data as CsvRow[]).filter((row) => {
              const artistName = row.artistName ?? row.ArtistName ?? "";
              const assetSound =
                row.assetSound ?? row.AssetSound ?? row.sound ?? row.Sound ?? "";
              const key = `${artistName}|||${assetSound}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            const skippedCount = results.data.length - uniqueRows.length;

            // Step 3: Import new items from CSV
            const votableItems = uniqueRows.map((row) => ({
              id: `vi${Math.random().toString(36).substring(2, 15)}`,
              type: "song", // You can modify this or add a type column in CSV
              votingEventId: record.id,
              displayName: row.displayName ?? row.DisplayName ?? "",
              artistName: row.artistName ?? row.ArtistName ?? "",
              assetSound: row.assetSound ?? row.AssetSound ?? row.sound ?? row.Sound ?? "",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }));

            const importPromises = votableItems.map((item) =>
              dataProvider.create(`votingEvents/${record.id}/votableItems`, {
                data: item,
              })
            );

            await Promise.all(importPromises);

            notify(
              `Successfully imported ${votableItems.length} items` +
                (skippedCount > 0
                  ? ` (skipped ${skippedCount} duplicates)`
                  : ""),
              { type: "success" }
            );
            setFileName("");
          } catch (error) {
            console.error("Import error:", error);
            notify("Error importing items. Please check your CSV format.", {
              type: "error",
            });
          } finally {
            setImporting(false);
          }
        },
        error: (error) => {
          console.error("Parse error:", error);
          notify("Error parsing CSV file", { type: "error" });
          setImporting(false);
        },
      });
    } catch (error) {
      console.error("File upload error:", error);
      notify("Error uploading file", { type: "error" });
      setImporting(false);
    }

    // Reset input
    event.target.value = "";
  };

  if (!record?.id) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Save the voting event first before importing votable items.
      </Alert>
    );
  }

  return (
    <Box mt={3} mb={3}>
      <Typography variant="subtitle2" gutterBottom>
        Import Votable Items from CSV
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={2}
      >
        CSV should have columns: <strong>artistName</strong>,{" "}
        <strong>displayName</strong> (optional), <strong>assetSound</strong> (optional)
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        ⚠️ Importing will <strong>delete all existing items</strong> and replace
        them with the CSV data.
      </Alert>

      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="outlined"
          component="label"
          startIcon={
            importing ? <CircularProgress size={20} /> : <UploadIcon />
          }
          disabled={importing}
        >
          {importing ? "Importing..." : "Upload CSV"}
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileUpload}
            disabled={importing}
          />
        </Button>
        {fileName && (
          <Typography variant="body2" color="text.secondary">
            {fileName}
          </Typography>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="caption">
          Example CSV format:
          <br />
          <code>
            artistName,displayName,assetSound
            <br />
            "Afrojack - Polkadots",,"https://open.spotify.com/track/..."
            <br />
            "Artist - Song Title","Display Name","https://open.spotify.com/track/..."
          </code>
        </Typography>
      </Alert>
    </Box>
  );
};

// Share Template Preview Component
const ShareTemplatePreview = () => {
  const { watch } = useFormContext();
  const title = watch("title");
  const shareTemplateImage = watch("shareTemplateImage");
  const votesPerUser = watch("votesPerUser") || 10;

  const imageUrl = shareTemplateImage?.url;

  // Parse title - handle both old string format and new object format
  const getTitleData = () => {
    if (!title) {
      return {
        text: "",
        fontSize: 32,
        fontWeight: "bold",
        fontStyle: "normal",
      };
    }
    if (typeof title === "string") {
      return {
        text: title,
        fontSize: 32,
        fontWeight: "bold",
        fontStyle: "normal",
      };
    }
    return {
      text: title.text || "",
      fontSize: title.fontSize || 32,
      fontWeight: title.fontWeight || "bold",
      fontStyle: title.fontStyle || "normal",
    };
  };

  const titleData = getTitleData();

  if (!imageUrl && !titleData.text) {
    return null;
  }

  // Sample songs for preview - only show 3
  const sampleSongs = [
    "Song Title 1 - Artist Name",
    "Song Title 2 - Artist Name",
    "Song Title 3 - Artist Name",
  ];

  return (
    <Box
      mt={3}
      mb={3}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography variant="subtitle2" gutterBottom textAlign="center">
        Share Template Preview
      </Typography>
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 400,
          overflow: "hidden",
          backgroundColor: "#1a1a1a",
        }}
      >
        {/* Background Image */}
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt="Share template background"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: 0.8,
            }}
          />
        )}

        {/* Content Overlay */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: 3,
          }}
        >
          {/* Title at top with custom styling */}
          {titleData.text && (
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                color: "white",
                fontSize: `${titleData.fontSize}px`,
                fontWeight: titleData.fontWeight,
                fontStyle: titleData.fontStyle,
                textShadow: "3px 3px 6px rgba(0,0,0,0.9)",
                mb: 3,
                wordBreak: "break-word",
              }}
            >
              {titleData.text}
            </Typography>
          )}

          {/* Votes List - only 3 items */}
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {sampleSongs.map((song, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  padding: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  borderRadius: 1,
                  backdropFilter: "blur(10px)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    minWidth: 35,
                    textShadow: "3px 3px 6px rgba(0,0,0,0.9)",
                  }}
                >
                  {index + 1}.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "white",
                    flex: 1,
                    textShadow: "2px 2px 4px rgba(0,0,0,0.9)",
                  }}
                >
                  {song}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block", textAlign: "center" }}
      >
        Preview showing 3 sample votes. Users will see {votesPerUser} votes when they finish voting.
      </Typography>
    </Box>
  );
};

// Custom toolbar
const VotingEventToolbar = (props: any) => {
  const { isEdit } = props;

  return (
    <Toolbar {...props}>
      <SaveButton />
      {isEdit && <DeleteButton />}
    </Toolbar>
  );
};

// Shared Form Component - now accepts isEdit as prop
const VotingEventFormContent = ({ isEdit }: { isEdit: boolean }) => {
  // Only use useEditContext when in edit mode
  const editContext = isEdit ? useEditContext() : { record: undefined };
  const record = editContext.record;

  // Prepare the record with the image from shareTemplate (for Edit mode)
  const preparedRecord =
    isEdit && record
      ? {
          ...record,
          shareTemplateImage: record.shareTemplate?.imageUrl
            ? { url: record.shareTemplate.imageUrl, type: "image" }
            : null,
        }
      : record;

  // Default values for Create mode
  const defaultValues = !isEdit
    ? {
        title: {
          text: "",
          fontSize: 32,
          fontWeight: "bold",
          fontStyle: "normal",
        },
        votesPerUser: 10,
        isActive: false,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      }
    : undefined;

  return (
    <SimpleForm
      record={preparedRecord}
      defaultValues={defaultValues}
      toolbar={<VotingEventToolbar isEdit={isEdit} />}
    >
      <Box mb={2}>
        <Typography variant="h6">
          {isEdit ? "Edit Voting Event" : "Create New Voting Event"}
        </Typography>
        {isEdit && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            ⚠️ Be careful when editing active events. Changes will affect users
            currently voting.
          </Alert>
        )}
      </Box>

      <TitleStyleInput
        source="title"
        label="Event Title"
        validate={required()}
        helperText={
          !isEdit
            ? "e.g., Insomniac Radio ONE's 2025 Top 100 Countdown"
            : undefined
        }
      />

      <Box display="flex" gap={2}>
        <DateTimeInput
          source="startDate"
          label="Voting Opens"
          validate={required()}
        />
        <DateTimeInput
          source="endDate"
          label="Voting Closes"
          validate={required()}
        />
      </Box>

      <NumberInput
        source="votesPerUser"
        label="Votes Per User"
        validate={[required(), minValue(1)]}
        helperText="Fixed at 10 to match TOP-10 format"
        disabled
        defaultValue={10}
      />

      <FirebaseImageInput
        source="shareTemplateImage"
        label="Share Template Background"
        validate={required()}
        helperText="Upload the background image for the voting results"
        hidePreview={true}
      />

      {/* Live Preview of Share Template */}
      <ShareTemplatePreview />

      {/* CSV Import - only show in Edit mode when event is saved */}
      {isEdit && <VotableItemsCsvImport />}

      <BooleanInput
        source="isActive"
        label="Show VOTE Tab"
        helperText="Toggle the VOTE tab visibility on the website"
      />
    </SimpleForm>
  );
};

// Transform function for both Create and Edit
const transformData = (data: any, isEdit: boolean) => {
  // Handle title format - convert to string for storage if needed, or keep as object
  // Assuming you want to store the full title object
  const titleData =
    typeof data.title === "string"
      ? {
          text: data.title,
          fontSize: 32,
          fontWeight: "bold",
          fontStyle: "normal",
        }
      : data.title;

  const transformed = {
    ...data,
    title: titleData,
    shareTemplate: {
      imageUrl:
        data.shareTemplateImage?.url || data.shareTemplate?.imageUrl || "",
    },
    updatedAt: new Date().toISOString(),
  };

  // Add fields for Create mode
  if (!isEdit) {
    transformed.id = `ve${Math.random().toString(36).substring(2, 15)}`;
    transformed.votesPerUser = 10;
    transformed.createdAt = new Date().toISOString();
  } else {
    // Preserve votesPerUser during edit (default to 10 if not present)
    transformed.votesPerUser = data.votesPerUser ?? 10;
  }

  // Remove the temporary shareTemplateImage field
  delete transformed.shareTemplateImage;

  return transformed;
};

// Create Component
export const VotingEventCreate = (props: Omit<CreateProps, "children">) => {
  const redirect = useRedirect();

  const onSuccess = () => {
    redirect("list", "votingEvents");
  };

  return (
    <Create
      resource="votingEvents"
      transform={(data) => transformData(data, false)}
      mutationOptions={{ onSuccess }}
      {...props}
    >
      <VotingEventFormContent isEdit={false} />
    </Create>
  );
};

// Edit Component
export const VotingEventEdit = (props: Omit<EditProps, "children">) => {
  return (
    <Edit
      resource="votingEvents"
      transform={(data) => transformData(data, true)}
      mutationMode="pessimistic"
      {...props}
    >
      <VotingEventFormContent isEdit={true} />
    </Edit>
  );
};